"use client";

import { Suspense, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Button, Typography, CreatorTrustRow, VideoEmbedCard } from "@artha/ui";
import { useT } from "@/lib/useT";
import { cn } from "@/lib/utils";
import { guestAuthService } from "@/lib/guestAuthService";
import { getGuestStatus } from "@/modules/auth/services/guestAuthClient";
import {
  AUTH_ICON_BUTTON_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
  AuthResponsivePage,
  CREATOR_SOURCE_CONFIG,
  ESTIMATE_EARNINGS_PRIMARY_SOURCE_ID,
  ESTIMATE_EARNINGS_SECONDARY_SOURCE_IDS,
} from "@/modules/auth";
import { ROUTES } from "@/lib/routes";

const YouTubeWhiteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.9554 4.02841C19.2657 4.34071 19.4886 4.72904 19.6017 5.15453C20.0198 6.72498 20.0198 9.99998 20.0198 9.99998C20.0198 9.99998 20.0198 13.275 19.6017 14.8454C19.4886 15.2709 19.2657 15.6593 18.9554 15.9716C18.6451 16.2839 18.2582 16.5092 17.8335 16.625C16.2698 17.0454 10.0198 17.0454 10.0198 17.0454C10.0198 17.0454 3.76984 17.0454 2.2062 16.625C1.78144 16.5092 1.39455 16.2839 1.08424 15.9716C0.773933 15.6593 0.551091 15.2709 0.438018 14.8454C0.0198365 13.275 0.0198364 9.99998 0.0198364 9.99998C0.0198364 9.99998 0.0198365 6.72498 0.438018 5.15453C0.551091 4.72904 0.773933 4.34071 1.08424 4.02841C1.39455 3.7161 1.78144 3.49078 2.2062 3.37498C3.76984 2.95453 10.0198 2.95453 10.0198 2.95453C10.0198 2.95453 16.2698 2.95453 17.8335 3.37498C18.2582 3.49078 18.6451 3.7161 18.9554 4.02841ZM13.2017 10L7.9744 7.02615V12.9739L13.2017 10Z"
      fill="white"
    />
  </svg>
);

function EstimateEarningsContent() {
  const router = useRouter();
  const { auth, common } = useT();
  const [isRedirecting, setIsRedirecting] = useState(() => {
    // Check if running on client before accessing localStorage/guestAuthService
    if (typeof window !== "undefined") {
      return guestAuthService.isGuest();
    }
    return false;
  });

  // Returning guest: resume from last completed step
  useEffect(() => {
    if (!guestAuthService.isGuest()) return;

    getGuestStatus()
      .then((status) => {
        router.replace(status.redirectTo);
      })
      .catch(() => {
        guestAuthService.clearToken();
        setIsRedirecting(false);
      });
  }, [router]);

  const handlePlatform = (platformId: string) => {
    router.push(ROUTES.guestAddAccount(platformId));
  };

  if (isRedirecting) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#FFFAEF]">
        <div className="animate-pulse text-[#344054] font-medium">Loading...</div>
      </div>
    );
  }

  return (
    <AuthResponsivePage
      hero={
        <VideoEmbedCard
          src="https://www.youtube-nocookie.com/embed/6zo8XL0aRAQ?autoplay=1&mute=1&playsinline=1&controls=0&disablekb=1&fs=0&cc_load_policy=0&rel=0&iv_load_policy=3"
          title="YouTube video player"
          posterSrc="https://i.ytimg.com/vi/6zo8XL0aRAQ/maxresdefault.jpg"
          playLabel="Play creator-led hiring video"
          playIconSrc="https://artha.link/wp-content/uploads/2026/03/Play-button.svg"
        />
      }
      heroClassName="px-5 md:px-0"
    >
      <div className="mb-4">
        <Typography variant="h3" className="text-[24px] text-black font-bold tracking-tight">
          Discover Your Earnings Potential
        </Typography>
        <Typography variant="p" className="text-[#475467] text-[15px] font-medium leading-normal mt-1">
          Get an instant estimate of what you could earn with artha.link
        </Typography>
      </div>

      <div className="flex gap-[10px] items-center w-full mb-3.5">
        {ESTIMATE_EARNINGS_SECONDARY_SOURCE_IDS.map((platformId) => {
          const platform = CREATOR_SOURCE_CONFIG[platformId];
          const Icon = platform.estimateIcon;
          return (
            <button
              key={platformId}
              onClick={() => handlePlatform(platformId)}
              className={AUTH_ICON_BUTTON_CLASS}
            >
              <Icon className={platform.estimateIconClassName} />
            </button>
          );
        })}
      </div>

      <div className="pb-8">
        <Button
          onClick={() => handlePlatform(ESTIMATE_EARNINGS_PRIMARY_SOURCE_ID)}
          showShine
          className={cn(AUTH_PRIMARY_BUTTON_CLASS, "gap-[6px] bg-[#ff0302] text-white")}
        >
          <span className="relative z-10">{auth("continueWith", { provider: common("youtube") })}</span>
          <div className="flex items-center justify-center ml-1 relative z-10">
            <YouTubeWhiteIcon />
          </div>
        </Button>

        <CreatorTrustRow className="mt-6" />
      </div>
    </AuthResponsivePage>
  );
}

export default function EstimateEarningsPage() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen bg-[#FFFAEF]">
          <div className="animate-pulse text-[#344054] font-medium">Loading...</div>
        </div>
      }
    >
      <EstimateEarningsContent />
    </Suspense>
  );
}
